import React, { useCallback, useEffect, useState } from "react";

import { useQuery } from "@apollo/client/react/hooks/useQuery";
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  GetApp as GetAppIcon,
  ListAlt as ListAltIcon,
  Share as ShareIcon,
} from "@mui/icons-material";
import { Box, Button, Modal, Theme, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import { makeStyles, withStyles } from "tss-react/mui";

import DesktopIcon from "@/components/Icons/DesktopIcon";
import MobileIcon from "@/components/Icons/MobileIcon";
import ShareFormPanel from "@/components/ShareFormPanel";
import { graphql } from "@/gql";
import { MyFormFragment } from "@/gql/graphql";
import { colors } from "@/theme/overrides/colors";

interface Props {
  data: MyFormFragment | null;
  onClose: () => void;
  visibility: boolean;
  onDownload?: () => Promise<void>;
  isSending?: boolean;
}

const StyledButtonAction = withStyles(Button, {
  root: {
    width: "100%",
    borderWidth: 2,
    fontWeight: 700,
    borderColor: "#D4DBE5",
    "&:hover": {
      borderWidth: 2,
    },
  },
});

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    alignItems: "center",
    justifyContent: "center",
  },
  mainContainer: {
    display: "flex",
    height: "100%",
    padding: theme.spacing(8, 16, 8, 16),
  },
  leftContainer: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    width: 400,
    padding: theme.spacing(5, 4, 5, 4),
    backgroundColor: colors.white,
  },
  rightContainer: {
    width: "100%",
    backgroundColor: "#E8EEF8",
  },
  desktopView: {
    height: "100%",
    width: "100%",
    padding: theme.spacing(4, 18, 4, 18),
  },
  mobileView: {
    height: "100%",
    width: "100%",
    display: "grid",
    placeItems: "center",
  },
  shareView: {
    padding: theme.spacing(4, 4, 4, 4),
  },
  title: {
    fontSize: 24,
    fontWeight: 700,
  },
  author: {
    marginTop: 5,
    fontSize: 16,
    fontWeight: 600,
  },
  divider: {
    marginTop: 20,
    marginBottom: 20,
    height: 1,
    backgroundColor: "#C8CED8",
  },
  actions: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  loadingContainer: {
    display: "flex",
    width: "100%",
    height: "100%",
    justifyContent: "center",
    alignItems: "center",
  },
  desktopViewer: {
    border: 0,
    width: "100%",
    height: "100%",
    backgroundColor: colors.white,
  },
  mobileViewer: {
    border: 0,
    height: "80%",
    width: 395,
  },
  backButton: {
    fontWeight: 700,
    borderWidth: 2,
    borderColor: "#949BA8",
    color: "#949BA8",
    "&:hover": {
      borderWidth: 2,
    },
  },
  tabPanel: {
    height: "100%",
  },
  downloadButton: {
    width: "100%",
    borderWidth: 2,
    fontWeight: 700,
    borderColor: "#D4DBE5",
    "&:hover": {
      borderWidth: 2,
    },
  },
}));

const coachViewerIdQuery = graphql(`
  query CoachViewerId2 {
    coachViewer {
      id
    }
  }
`);

enum PreviewModeKind {
  Desktop = "DESKTOP",
  Mobile = "MOBILE",
}

const PreviewFormModal: React.FC<Props> = ({
  data,
  onClose,
  visibility,
  isSending,
}) => {
  const [currentPanelIndex, setCurrentPanelIndex] = useState(0);
  const [previewMode, setPreviewMode] = useState<PreviewModeKind>(
    PreviewModeKind.Desktop
  );
  const { classes } = useStyles();
  const { data: coach } = useQuery(coachViewerIdQuery);
  const coachId = coach?.coachViewer.id;

  useEffect(() => {
    if (isSending) return setCurrentPanelIndex(1);
  }, [isSending]);

  const _renderPreview = useCallback(() => {
    if (currentPanelIndex !== 0) return;

    if (previewMode === PreviewModeKind.Desktop) {
      return (
        <div className={classes.desktopView}>
          <iframe className={classes.desktopViewer} src={data?.previewUrl} />
        </div>
      );
    } else {
      return (
        <div className={classes.mobileView}>
          <Box
            width={375}
            height={"100%"}
            sx={{ position: "relative", paddingTop: 24, overflowX: "hidden" }}
          >
            <Box
              width={"100%"}
              height={88}
              display={"flex"}
              alignItems={"end"}
              justifyContent={"center"}
              sx={{
                position: "absolute",
                top: 48,
                background: "linear-gradient(to bottom, #FF972C, #FFBA1B)",
                paddingBottom: 2,
                borderRadius: "0 0 24px 24px",
              }}
            >
              <ArrowBackIcon
                sx={{ position: "absolute", left: 10, color: colors.white }}
                fontSize="small"
              />
              <Typography sx={{ fontWeight: "bold", color: colors.white }}>
                Forms
              </Typography>
            </Box>
            <iframe className={classes.mobileViewer} src={data?.previewUrl} />
          </Box>
        </div>
      );
    }
  }, [previewMode, currentPanelIndex]);

  const _handlePreview = () => {
    setCurrentPanelIndex(0);
    setPreviewMode((prev) => {
      if (prev === PreviewModeKind.Desktop) return PreviewModeKind.Mobile;
      else return PreviewModeKind.Desktop;
    });
  };

  return (
    <Modal
      open={visibility}
      onClose={() => {
        onClose();
      }}
      className={classes.root}
    >
      <div className={classes.mainContainer}>
        <div className={classes.leftContainer}>
          <Box display="flex" flexDirection="column" flexGrow={1}>
            <Typography className={classes.title} variant="h6">
              {data?.title}
            </Typography>
            <Typography className={classes.author} variant="body2">
              Created by: {data?.coach.profile?.fullName}
            </Typography>
            <div className={classes.divider} />
            <div className={classes.actions}>
              <StyledButtonAction
                startIcon={
                  previewMode === PreviewModeKind.Desktop ? (
                    <MobileIcon />
                  ) : (
                    <DesktopIcon />
                  )
                }
                variant="outlined"
                style={{
                  borderColor: currentPanelIndex == 0 ? "#26C9A7" : "#D4DBE5",
                }}
                onClick={_handlePreview}
              >
                {previewMode === PreviewModeKind.Desktop
                  ? "Mobile View"
                  : "Desktop View"}
              </StyledButtonAction>
              {coachId == data?.coach.id && (
                <Link
                  style={{ textDecoration: "none" }}
                  to={`/forms/${data?.id}/edit`}
                  rel="noreferrer"
                >
                  <StyledButtonAction
                    startIcon={<EditIcon fontSize="small" />}
                    variant="outlined"
                  >
                    Edit
                  </StyledButtonAction>
                </Link>
              )}
              <StyledButtonAction
                startIcon={<ShareIcon fontSize="small" />}
                variant="outlined"
                style={{
                  borderColor: currentPanelIndex == 1 ? "#26C9A7" : "#D4DBE5",
                }}
                onClick={() => setCurrentPanelIndex(1)}
              >
                Send
              </StyledButtonAction>
              {/* Download button doesn't use StyledButtonAction because
                  `withStyles` destroys `component` prop */}
              <Button
                className={classes.downloadButton}
                component="a"
                href={`${data?.pdfUrl}&download`}
                target="_blank"
                startIcon={<GetAppIcon fontSize="small" />}
                variant="outlined"
              >
                Download
              </Button>
            </div>
          </Box>
          <div>
            <div className={classes.divider} />
            <Button
              className={classes.backButton}
              variant="outlined"
              startIcon={<ArrowBackIcon fontSize="small" />}
              onClick={onClose}
            >
              My Form
            </Button>
          </div>
        </div>
        <div className={classes.rightContainer}>
          {_renderPreview()}
          {currentPanelIndex == 1 && (
            <div className={classes.shareView}>
              <ShareFormPanel onClose={onClose} formId={data?.id} />
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default PreviewFormModal;
